import { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, CheckCircleIcon, TrashIcon, InformationCircleIcon } from '@heroicons/react/16/solid';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { useSLBDirectorPanel } from './SLBDirectorPanel.hook';
import { tSLBDirectorPanelProps } from './SLBDirectorPanel.types';
import './SLBDirectorPanel.styles.scss';

export default function SLBDirectorPanel(props: tSLBDirectorPanelProps) {
  const { chatId, workspaceId, onClose } = props;
  const { t } = useTranslation('chat/chat');
  const {
    instructions,
    text,
    setText,
    isSending,
    isDeleting,
    isInitialized,
    confirmation,
    handleSend,
    handleDelete,
    handleClearAll,
  } = useSLBDirectorPanel({ chatId, workspaceId });

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const EXAMPLES = [
    t('slbDirectorPanel.examples.discount'),
    t('slbDirectorPanel.examples.redirect'),
    t('slbDirectorPanel.examples.tone'),
  ];

  // Auto-grow textarea up to 80px
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 80)}px`;
  }, [text]);

  function handleChipClick(chip: string) {
    setText(chip);
    textareaRef.current?.focus();
  }

  const hasInstructions = instructions.length > 0;

  return (
    <div className="SLBDirectorPanel">
      {/* Header row */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-base-200">
        <SparklesIcon className="w-4 h-4 text-violet-500 shrink-0" aria-hidden="true" />
        <span className="flex-1 text-[13px] font-semibold text-base-content">
          {t('slbDirectorPanel.title')}
        </span>
        <button
          onClick={onClose}
          className="shrink-0 p-0.5 rounded hover:bg-base-200 transition-colors focus-visible:outline-2 focus-visible:outline-violet-500"
          aria-label={t('slbDirectorPanel.close')}
        >
          <XMarkIcon className="w-3.5 h-3.5 text-base-content/40" />
        </button>
      </div>

      {/* Confirmation strip — shows the actual instruction that was sent */}
      {confirmation && (
        <div className="SLBDirectorPanel__Confirmation">
          <CheckCircleIcon className="w-3.5 h-3.5 text-success shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="text-[10px] text-base-content/50 block">
              {t('slbDirectorPanel.confirmationLabel')}
            </span>
            <span className="text-xs text-base-content/80 font-medium block truncate">
              &quot;{confirmation}&quot;
            </span>
          </div>
        </div>
      )}

      {/* Active instructions — shown inline (no tab switch) whenever any exist */}
      {hasInstructions && (
        <div className="px-3 pt-2 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-semibold text-base-content/60 uppercase tracking-wide">
              {t('slbDirectorPanel.count', { count: instructions.length })}
            </span>
            <button
              onClick={() => void handleClearAll()}
              disabled={isDeleting}
              className="text-[10px] text-red-500 hover:text-red-700 disabled:opacity-50 transition-colors"
            >
              {t('slbDirectorPanel.clearAll')}
            </button>
          </div>
          <ul className="SLBDirectorPanel__ActiveList space-y-1">
            {instructions.map((inst, idx) => (
              <li
                key={inst.id}
                className="group flex items-start gap-2 bg-base-100 rounded-lg border border-base-200 px-2 py-1.5 hover:border-base-300 transition-colors"
              >
                <span className="shrink-0 w-4 text-right text-[11px] font-medium tabular-nums text-base-content/30">
                  {idx + 1}
                </span>
                <span className="flex-1 text-xs text-base-content/80 leading-tight">
                  {inst.text}
                </span>
                <button
                  onClick={() => void handleDelete(inst.id)}
                  disabled={isDeleting}
                  className="shrink-0 p-0.5 rounded hover:bg-red-50 disabled:opacity-50 transition-colors focus-visible:outline-2 focus-visible:outline-red-400"
                  aria-label={t('slbDirectorPanel.deleteInstruction')}
                >
                  <TrashIcon className="w-3 h-3 text-red-300 group-hover:text-red-400 transition-colors" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* New instruction — always available. Divider only when it follows the active list. */}
      <div
        className={`px-3 py-2.5 space-y-2 ${
          hasInstructions ? 'border-t border-base-200' : ''
        }`}
      >
        {/* Empty state framing vs. section label, so the input always reads as an intentional section */}
        {isInitialized && !hasInstructions && (
          <div className="space-y-0.5">
            <span className="block text-[13px] font-semibold text-base-content">
              {t('slbDirectorPanel.emptyTitle')}
            </span>
            <span className="block text-[11px] leading-snug text-base-content/50">
              {t('slbDirectorPanel.emptySubtitle')}
            </span>
          </div>
        )}
        {hasInstructions && (
          <span className="block text-[10px] font-semibold uppercase tracking-wide text-base-content/60">
            {t('slbDirectorPanel.addLabel')}
          </span>
        )}

        {/* Example suggestions — empty state only, before the user starts typing */}
        {isInitialized && !hasInstructions && !text && (
          <div className="flex flex-col gap-1">
            {EXAMPLES.map((example) => (
              <button
                key={example}
                onClick={() => handleChipClick(example)}
                className="text-left px-2.5 py-1.5 rounded-lg text-[11px] text-base-content/70 bg-base-100 border border-base-200 hover:bg-violet-50 hover:border-violet-200 hover:text-violet-700 transition-all active:scale-[0.98]"
              >
                <span className="text-violet-400 mr-1">→</span>
                {example}
              </button>
            ))}
          </div>
        )}

        {/* Input with integrated send button — flex wrapper centers the button against the field */}
        <div className="relative flex items-center gap-1 rounded-lg border border-base-300 bg-base-100 pl-3 pr-1.5 transition-colors focus-within:border-violet-400 focus-within:ring-1 focus-within:ring-violet-200">
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && text.trim()) {
                e.preventDefault();
                void handleSend();
              }
            }}
            placeholder={t('slbDirectorPanel.placeholder')}
            maxLength={500}
            rows={1}
            className="flex-1 resize-none border-0 bg-transparent py-2 text-xs text-base-content placeholder:text-base-content/30 focus:outline-none overflow-hidden min-h-[34px] max-h-[80px]"
          />
          {text.length > 450 && (
            <span className="self-end mb-1.5 text-[9px] text-red-500">
              {text.length}/500
            </span>
          )}
          <button
            onClick={() => void handleSend()}
            disabled={!text.trim() || isSending}
            className="shrink-0 w-7 h-7 rounded-lg bg-violet-600 text-white flex items-center justify-center hover:bg-violet-700 disabled:bg-violet-200 disabled:text-violet-500 disabled:cursor-not-allowed active:scale-95 transition-all focus-visible:outline-2 focus-visible:outline-violet-600"
            aria-label={t('slbDirectorPanel.sendAriaLabel')}
          >
            {isSending ? (
              <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            ) : (
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5"
                />
              </svg>
            )}
          </button>
        </div>

        <p className="text-[10px] text-base-content/40">
          {t('slbDirectorPanel.hint')}
        </p>
        <p className="flex items-start gap-1 text-[10px] leading-snug text-base-content/45">
          <InformationCircleIcon className="w-3 h-3 shrink-0 mt-px" aria-hidden="true" />
          {t('slbDirectorPanel.scopeNote')}
        </p>
      </div>
    </div>
  );
}
